import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { BLOCKS } from '@contentful/rich-text-types'
import type { Options } from '@contentful/rich-text-react-renderer'
import { LayoutContentWrapper } from '../LayoutContentWrapper/LayoutContentWrapper'
import { StyledDisclaimer } from '../Disclaimer/Disclaimer'
import { breakpointFrom, breakpointUpTo } from '../../utils/breakpoints'

export type SectionWithTitleProps = {
  children?: React.ReactNode
  title?: string
  textContent?: any
  disclaimers?: string[]
}
const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 954px;
  margin: 0 auto;
  margin-bottom: 46px;

  h2 {
    ${({ theme }) => theme.typography.LrgH2};
  }

  .text {
    margin-top: ${({ theme }) => theme.space[4]};
    margin-bottom: 0;
    ${({ theme }) => theme.typography.LrgBody1};
  }

  a {
    color: ${({ theme }) => theme.colors.primary};

    :hover,
    :focus {
      text-decoration: underline;
    }
  }

  ${({ theme }) => breakpointUpTo(theme.breakpoints.lg)} {
    margin-bottom: 36px;
    h2 {
      ${({ theme }) => theme.typography.MedH2};
    }
    .text {
      ${({ theme }) => theme.typography.MedBody1};
    }
  }

  ${({ theme }) => breakpointUpTo(theme.breakpoints.md)} {
    margin-bottom: ${({ theme }) => theme.space[4]};

    h2 {
      ${({ theme }) => theme.typography.SmH2};
    }
    .text {
      ${({ theme }) => theme.typography.SmBody1};
    }
  }

  ${({ theme }) => breakpointFrom(theme.breakpoints.xl)} {
    margin-bottom: 54px;

    h2 {
      ${({ theme }) => theme.typography.XLH2};
    }
    .text {
      ${({ theme }) => theme.typography.XLBody1};
    }
  }
`
const StyledSection = styled.section`
  margin-top: 100px;
  margin-bottom: 100px;
  text-align: center;

  ${({ theme }) => breakpointUpTo(theme.breakpoints.lg)} {
    margin-top: 72px;
    margin-bottom: 72px;
  }

  ${({ theme }) => breakpointUpTo(theme.breakpoints.md)} {
    margin-top: 36px;
    margin-bottom: 36px;
  }
`

type TextProps = { children: ReactNode }

const Text = ({ children }: TextProps) => <p className="text">{children}</p>

const rendererOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Text>{children}</Text>,
  },
}

export const SectionWithTitle = ({
  children,
  title,
  textContent,
  disclaimers,
}: SectionWithTitleProps) => (
  <StyledSection>
    <LayoutContentWrapper>
      <TextWrapper>
        {textContent ? (
          <>
            <h2>{title}</h2>
            {renderRichText(textContent, rendererOptions)}
            {disclaimers
              ? disclaimers.map((disclaimer) => (
                  <StyledDisclaimer>{disclaimer}</StyledDisclaimer>
                ))
              : null}
          </>
        ) : (
          <h2>{title}</h2>
        )}
      </TextWrapper>
      {children}
    </LayoutContentWrapper>
  </StyledSection>
)
